import { type IGetSocialApplyResponse, type TGetFupLogosResponse, useSuspenseGetFupLogos } from "@api";
import type { IDocument } from "@bbj-types/document";
import type { PartnerDecision } from "@bbj-types/socialApply/enum";
import SendIcon from "@mui/icons-material/Send";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Stack, Typography } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { ResponsiveButton, SuspenseHandler } from "@styleguide";
import { useFupPartnerForm } from "./logics";
import { PartnerFeedbackForm } from "./partnerFeedback";
import { FormVisualize } from "./visualize";

const isFupSocialApply = (socialApply: IGetSocialApplyResponse): socialApply is Required<IGetSocialApplyResponse> =>
  Boolean(socialApply.fup);

interface IFupPartnerFormContentProps {
  displayTitle?: boolean;
  socialApply: Required<IGetSocialApplyResponse>;
  fupLogos: TGetFupLogosResponse;
  onSendFup: (result: { fupDocument: IDocument; decision: PartnerDecision }) => void;
}

const FupPartnerFormContent = ({ socialApply, fupLogos, onSendFup, displayTitle }: IFupPartnerFormContentProps) => {
  const {
    control,
    getValues,
    setValue,
    resetField,
    onDisplayPreviewPdf,
    loadingPreview,
    onSendFupToOperator,
    loadingSend,
    sponsorsLogo,
  } = useFupPartnerForm(socialApply, onSendFup, fupLogos);

  return (
    <Stack
      sx={{
        height: "100%",
        justifyContent: "space-between",
      }}
    >
      <Stack
        sx={{
          gap: 4,
          overflow: "hidden",
        }}
      >
        {displayTitle && <Typography variant="h4">Prescription pour une offre</Typography>}
        <Stack
          sx={{
            gap: 4,
            overflow: "auto",
            pr: 2,
          }}
        >
          <FormVisualize fup={socialApply.fup} />
          <PartnerFeedbackForm
            control={control}
            getValues={getValues}
            setValue={setValue}
            resetField={resetField}
            sponsorsLogo={sponsorsLogo}
          />
        </Stack>
      </Stack>
      <Stack
        direction="row"
        sx={{
          gap: 1,
          justifyContent: "end",
          pt: 2,
          pr: 2,
        }}
      >
        <ResponsiveButton
          startIcon={<VisibilityOutlinedIcon />}
          variant="outlined"
          color="primary"
          onClick={onDisplayPreviewPdf}
          loading={loadingPreview}
        >
          aperçu pdf
        </ResponsiveButton>
        <ResponsiveButton
          startIcon={<SendIcon />}
          variant="contained"
          color="primary"
          onClick={onSendFupToOperator}
          loading={loadingSend}
        >
          transmettre au référent
        </ResponsiveButton>
      </Stack>
    </Stack>
  );
};

interface IFupPartnerFormSuspenseProps {
  displayTitle?: boolean;
  socialApply: Required<IGetSocialApplyResponse>;
  getFupLogos: () => TGetFupLogosResponse;
  onSendFup: (result: { fupDocument: IDocument; decision: PartnerDecision }) => void;
}

const FupPartnerFormSuspense = ({
  displayTitle,
  socialApply,
  getFupLogos,
  onSendFup,
}: IFupPartnerFormSuspenseProps) => {
  const fupLogos = getFupLogos();

  return (
    <FupPartnerFormContent
      socialApply={socialApply}
      fupLogos={fupLogos}
      onSendFup={onSendFup}
      displayTitle={displayTitle}
    />
  );
};

interface IFupPartnerFormProps {
  displayTitle?: boolean;
  socialApply: IGetSocialApplyResponse;
  onSendFup: (result: { fupDocument: IDocument; decision: PartnerDecision }) => void;
}

export const FupPartnerForm = ({ socialApply, onSendFup, displayTitle }: IFupPartnerFormProps) => {
  const { user } = useGlobalContext();
  const getFupLogos = useSuspenseGetFupLogos();

  return isFupSocialApply(socialApply) ? (
    <SuspenseHandler
      redirect={user.roleV2.value === "fullPartner" ? "/partners/structure/dashboard" : "/partners/structure/default"}
      slotProps={{ box: { height: "100%" } }}
    >
      <FupPartnerFormSuspense
        displayTitle={displayTitle}
        socialApply={socialApply}
        getFupLogos={getFupLogos}
        onSendFup={onSendFup}
      />
    </SuspenseHandler>
  ) : (
    <Stack
      sx={{
        height: "100%",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Typography>Il n'existe pas de prescription pour cette offre sociale.</Typography>
    </Stack>
  );
};
